/**
 * Badge service — awards and retrieves client badges
 */
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createLogger } from '../utils/logger';
import { fireCelebration } from '../utils/fireCelebration';
import { CELEBRATION_TYPES } from '../data/celebrations';

const log = createLogger('badgeService');

const BADGES_COLLECTION = 'userBadges';

/**
 * Award a badge to a user (idempotent — uses deterministic doc ID)
 * Dispatches a 'badge-awarded' CustomEvent on the window when successful.
 * @param {Object} params
 * @param {string} params.userId
 * @param {string} params.badgeId
 * @param {string} params.name
 * @param {string} params.description
 * @param {string} params.icon
 * @returns {Promise<string|null>} Badge doc ID or null if already exists
 */
export const awardBadge = async ({ userId, badgeId, id, name, description, icon }) => {
  badgeId = badgeId || id;
  // Use deterministic doc ID so we can check with getDoc instead of a query
  const docId = `${userId}_${badgeId}`;
  const docRef = doc(db, BADGES_COLLECTION, docId);

  const existing = await getDoc(docRef);
  if (existing.exists()) {
    log.info('Badge already awarded, skipping', { userId, badgeId });
    return null;
  }

  await setDoc(docRef, {
    userId,
    badgeId,
    name,
    description,
    icon,
    awardedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  log.info('Badge awarded', { userId, badgeId, docId });

  // Notify the UI via custom event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('badge-awarded', {
      detail: { badgeId, name, description, icon },
    }));
    fireCelebration(CELEBRATION_TYPES.BADGE_EARNED, { message: `Badge: ${name}` });
  }

  return docId;
};

/**
 * Get all badges for a user
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export const getUserBadges = async (userId) => {
  const q = query(
    collection(db, BADGES_COLLECTION),
    where('userId', '==', userId),
    orderBy('awardedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
